// src/hooks/useAdminInvoices.js
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

const useAdminInvoices = () => {
  const { user } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  // ── Live invoices (sent) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) {
      setInvoices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, 'invoices'), orderBy('sentAt', 'desc')),
      (snapshot) => {
        setInvoices(
          snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              sentAt: data.sentAt?.toDate() || null,
              dueDate: data.dueDate?.toDate() || null,
            };
          })
        );
        setLoading(false);
      },
      (err) => {
        console.error('Error loading invoices:', err);
        setError('Failed to load invoices');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user?.uid]);

  // ── Live drafts ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) {
      setDrafts([]);
      setDraftsLoading(false);
      return;
    }
    setDraftsLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, 'invoiceDrafts'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setDrafts(
          snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || null,
              dueDate: data.dueDate?.toDate() || null,
            };
          })
        );
        setDraftsLoading(false);
      },
      (err) => {
        console.error('Error loading drafts:', err);
        setError('Failed to load drafts');
        setDraftsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user?.uid]);

  // ── Shared payload builder ────────────────────────────────────────────────
  const buildPayload = useCallback(
    (invoiceData) => {
      const daysUntilDue = invoiceData.dueDate
        ? Math.max(
            1,
            Math.ceil(
              (new Date(invoiceData.dueDate) - new Date()) / (1000 * 60 * 60 * 24)
            )
          )
        : 30;

      return {
        email: invoiceData.customerEmail,
        items: invoiceData.lineItems.map((item) => ({
          amount: Math.round(item.amount * 100), // dollars → cents
          currency: 'usd',
          quantity: item.quantity || 1,
          description: item.description,
        })),
        daysUntilDue,
        customerName: invoiceData.customerName,
        memo: invoiceData.memo || null,
        totalAmount: invoiceData.lineItems.reduce(
          (sum, item) => sum + item.amount * (item.quantity || 1),
          0
        ),
        createdBy: user.uid,
      };
    },
    [user]
  );

  // ── Send invoice immediately ──────────────────────────────────────────────
  const sendInvoice = useCallback(
    async (invoiceData) => {
      setSending(true);
      setError(null);
      try {
        const docRef = await addDoc(collection(db, 'invoices'), {
          ...buildPayload(invoiceData),
          sentAt: serverTimestamp(),
        });
        return { success: true, invoiceId: docRef.id };
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [buildPayload]
  );

  // ── Save new draft ────────────────────────────────────────────────────────
  const saveDraft = useCallback(
    async (invoiceData) => {
      setSending(true);
      setError(null);
      try {
        const docRef = await addDoc(collection(db, 'invoiceDrafts'), {
          ...buildPayload(invoiceData),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        return { success: true, draftId: docRef.id };
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [buildPayload]
  );

  // ── Update existing draft ─────────────────────────────────────────────────
  const updateDraft = useCallback(
    async (draftId, invoiceData) => {
      setSending(true);
      setError(null);
      try {
        await updateDoc(doc(db, 'invoiceDrafts', draftId), {
          ...buildPayload(invoiceData),
          updatedAt: serverTimestamp(),
        });
        return { success: true };
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [buildPayload]
  );

  // ── Approve (send) one or many drafts ────────────────────────────────────
  const approveDrafts = useCallback(async (draftsToApprove) => {
    const list = Array.isArray(draftsToApprove) ? draftsToApprove : [draftsToApprove];
    setSending(true);
    setError(null);
    try {
      await Promise.all(
        list.map(async (draft) => {
          await addDoc(collection(db, 'invoices'), {
            email: draft.email,
            items: draft.items,
            daysUntilDue: draft.daysUntilDue,
            customerName: draft.customerName,
            memo: draft.memo || null,
            totalAmount: draft.totalAmount,
            createdBy: draft.createdBy,
            sentAt: serverTimestamp(),
          });
          await deleteDoc(doc(db, 'invoiceDrafts', draft.id));
        })
      );
      return { success: true, count: list.length };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSending(false);
    }
  }, []);

  // ── Delete a draft ────────────────────────────────────────────────────────
  const deleteDraft = useCallback(async (draftId) => {
    setError(null);
    try {
      await deleteDoc(doc(db, 'invoiceDrafts', draftId));
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const getStats = useCallback(() => {
    const total = invoices.length;
    const paid = invoices.filter((i) => i.stripeInvoiceStatus === 'paid').length;
    const sent = invoices.filter(
      (i) => !i.stripeInvoiceStatus || i.stripeInvoiceStatus === 'open'
    ).length;
    const failed = invoices.filter(
      (i) => i.stripeInvoiceStatus === 'payment_failed'
    ).length;
    const totalRevenue = invoices
      .filter((i) => i.stripeInvoiceStatus === 'paid')
      .reduce((sum, i) => sum + (i.totalAmount || 0), 0);
    const totalOutstanding = invoices
      .filter((i) => !i.stripeInvoiceStatus || i.stripeInvoiceStatus === 'open')
      .reduce((sum, i) => sum + (i.totalAmount || 0), 0);

    return { total, paid, sent, failed, totalRevenue, totalOutstanding };
  }, [invoices]);

  return {
    invoices,
    drafts,
    loading,
    draftsLoading,
    sending,
    error,
    clearError,
    sendInvoice,
    saveDraft,
    updateDraft,
    approveDrafts,
    deleteDraft,
    getStats,
  };
};

export default useAdminInvoices;